import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Avatar,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useMutation } from "@apollo/client";
import { REQUEST_AI_EVIDENCE_ACCESS } from "../../../apis/integrated-evidence/aiEvidenceApis";
import { initials } from "../../../utils/aiEvidenceOwners";
import RequestReviewDialog from "../../../components/RequestReviewDialog/RequestReviewDialog";

const AIEvidenceAccessRequest = ({ checkId, totalCount = 0, owners = [] }) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const info = tc.status.info;

  // A lone owner is selected implicitly; with several, start empty so the
  // choice is deliberate.
  const [selected, setSelected] = useState(() =>
    owners.length > 1
      ? new Set()
      : new Set(owners.map((o) => o.employeeDataId)),
  );
  const [result, setResult] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const [requestAccess, { loading: sending }] = useMutation(
    REQUEST_AI_EVIDENCE_ACCESS,
  );

  const handleRequest = async () => {
    try {
      const res = await requestAccess({
        variables: { checkId, ownerIds: Array.from(selected) },
      });
      setResult(res?.data?.requestAIEvidenceAccess || null);
    } catch (err) {
      setResult({ success: false, message: "Could not send the request." });
    }
  };

  const allSelected = useMemo(
    () => owners.length > 0 && selected.size === owners.length,
    [owners.length, selected.size],
  );

  const toggleOwner = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === owners.length
        ? new Set()
        : new Set(owners.map((o) => o.employeeDataId)),
    );
  };

  return (
    <Box sx={{ px: 3, py: 2.5 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: "14.5px", fontWeight: 600, color: tc.text.strong }}
        >
          {totalCount} evidence{" "}
          {totalCount === 1 ? "suggestion" : "suggestions"} found for this check
        </Typography>
        <Typography
          sx={{ fontSize: "13px", color: tc.text.secondary, mt: 0.25 }}
        >
          {owners.length === 1
            ? "Managed by the data owner below. Request access to review it."
            : "Managed by the data owners below. Request access to review them."}
        </Typography>
      </Box>

      <Box
        sx={{
          border: `1px solid ${tc.border.subtle}`,
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            backgroundColor: tc.background.header,
            borderBottom: `1px solid ${tc.border.subtle}`,
          }}
        >
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={selected.size > 0 && !allSelected}
            onChange={toggleAll}
            sx={{ p: 0.5 }}
          />
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: tc.text.secondary,
              letterSpacing: "0.02em",
            }}
          >
            {selected.size === owners.length
              ? `All ${owners.length} data ${
                  owners.length === 1 ? "owner" : "owners"
                }`
              : `${selected.size} of ${owners.length} selected`}
          </Typography>
        </Box>

        {owners.map((owner, index) => {
          const label = owner.name || owner.email || "Unknown owner";
          const isChecked = selected.has(owner.employeeDataId);
          return (
            <Box
              key={owner.employeeDataId || index}
              onClick={() => toggleOwner(owner.employeeDataId)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                cursor: "pointer",
                borderBottom:
                  index < owners.length - 1
                    ? `1px solid ${tc.border.subtle}`
                    : "none",
                backgroundColor: isChecked
                  ? alpha(info.main, 0.04)
                  : "transparent",
                transition: "background-color 0.15s ease",
                "&:hover": { backgroundColor: tc.background.hover },
              }}
            >
              <Checkbox size="small" checked={isChecked} sx={{ p: 0.5 }} />
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  fontSize: "11px",
                  fontWeight: 600,
                  bgcolor: alpha(info.main, 0.14),
                  color: info.text,
                }}
              >
                {initials(owner.name || owner.email)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "13.5px",
                    fontWeight: 500,
                    color: tc.text.strong,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
                {owner.name && owner.email && (
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      color: tc.text.disabled,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {owner.email}
                  </Typography>
                )}
                {owner.integrations && (
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      color: tc.text.disabled,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {owner.integrations}
                  </Typography>
                )}
              </Box>
              <Tooltip
                arrow
                title={`${owner.pendingCount} suggestion${
                  owner.pendingCount === 1 ? "" : "s"
                } managed by this owner`}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: info.text,
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                  }}
                >
                  {owner.pendingCount}
                </Typography>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          mt: 2,
        }}
      >
        {result?.message && (
          <Typography
            sx={{
              fontSize: "12.5px",
              color: result.success ? tc.button.success.dark : tc.status.error?.text || "#B91C1C",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mr: "auto",
            }}
          >
            {result.success && (
              <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
            )}
            {result.message}
          </Typography>
        )}
        {/* The other way out: leave the evidence with its owner and ask them
            to review it themselves. */}
        <Button
          variant="outlined"
          disableElevation
          onClick={() => setReviewOpen(true)}
          sx={{
            fontWeight: 500,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "14px",
            px: 2,
            py: 1,
            whiteSpace: "nowrap",
            backgroundColor: tc.background.main,
            borderColor: tc.border.strong,
            color: tc.text.strong,
            "&:hover": {
              borderColor: tc.text.secondary,
              backgroundColor: tc.background.hover,
            },
          }}
        >
          Ask them to review
        </Button>

        <Button
          variant="contained"
          disableElevation
          disabled={selected.size === 0 || sending || result?.success}
          onClick={handleRequest}
          endIcon={
            sending ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            bgcolor: "#3B82F6",
            color: "#fff",
            fontWeight: 500,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "14px",
            px: 2,
            py: 1,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#2563EB",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            },
            "&:disabled": {
              backgroundColor: tc.background.disabled,
              color: tc.text.disabled,
            },
          }}
        >
          {result?.success
            ? "Request sent"
            : sending
              ? "Sending..."
              : `Request access${
                  selected.size > 0 && selected.size < owners.length
                    ? ` (${selected.size})`
                    : ""
                }`}
        </Button>
      </Box>

      <RequestReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        checkId={checkId}
      />
    </Box>
  );
};

export default AIEvidenceAccessRequest;
